import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Badge,
  Button,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  PageHeader,
  Pagination,
  SelectField,
  toast,
  type Column,
} from '@eon/ui';
import { hooks, type IdTag } from '@eon/api-client';
import {
  Ban,
  CalendarPlus,
  MoreVertical,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { formatDate } from '@eon/utils';
import { usePaginationLabels } from '@/lib/use-pagination-labels';
import { useTableSort } from '@/lib/table-sort';
import { CreateIdTagDialog } from '@/components/CreateIdTagDialog';
import { UpdateIdTagDialog } from '@/components/UpdateIdTagDialog';
import { ExtendIdTagExpiryDialog } from '@/components/ExtendIdTagExpiryDialog';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const ALL_USERS = '__all__';

const STATUS_VARIANT: Record<IdTag['status'], 'success' | 'destructive' | 'secondary'> = {
  active: 'success',
  blocked: 'destructive',
  expired: 'secondary',
};

function isTagBlocked(tag: IdTag): boolean {
  return tag.isBlocked || tag.maxActiveTransactions === 0;
}

function formatMaxTx(tag: IdTag, t: (k: string) => string): string {
  if (tag.maxActiveTransactions < 0) return t('idTag.maxTx.-1');
  if (tag.maxActiveTransactions === 0) return t('idTag.maxTx.0');
  return String(tag.maxActiveTransactions);
}

export function IdTagsPage() {
  const { t } = useTranslation();
  const labels = usePaginationLabels();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [q, setQ] = useState('');
  const [userId, setUserId] = useState(ALL_USERS);
  const [creating, setCreating] = useState(false);
  const [editTag, setEditTag] = useState<IdTag | null>(null);
  const [extendTag, setExtendTag] = useState<IdTag | null>(null);
  const [deleteTag, setDeleteTag] = useState<IdTag | null>(null);

  const { sort, onSortChange, sortParams } = useTableSort(() => setPage(1));
  const list = hooks.useAdminIdTags({
    page,
    pageSize,
    q: q.trim() || undefined,
    userId: userId === ALL_USERS ? undefined : userId,
    ...sortParams,
  });
  const users = hooks.useAdminUsers({ page: 1, pageSize: 200 });
  const block = hooks.useBlockIdTag();
  const unblock = hooks.useUnblockIdTag();
  const del = hooks.useDeleteIdTag();

  const userFilterOptions = [
    { value: ALL_USERS, label: t('filters.all') },
    ...(users.data?.data ?? []).map((u) => ({
      value: u.id,
      label: u.name || u.email || u.id,
    })),
  ];

  const columns: Column<IdTag>[] = [
    {
      key: 'tag',
      header: t('idTag.tag'),
      sortable: true,
      sortValue: (tag) => tag.value,
      cell: (tag) => (
        <div>
          <span className="font-mono text-xs font-medium">{tag.value}</span>
          {tag.name !== tag.value ? (
            <span className="text-muted-foreground block text-xs">{tag.name}</span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'type',
      header: t('idTag.tagType'),
      cell: (tag) => (
        <Badge variant={tag.isPhysical ? 'outline' : 'secondary'}>
          {tag.tagType === 'Physical' ? t('idTag.physical') : t('idTag.digital')}
        </Badge>
      ),
    },
    {
      key: 'parent',
      header: t('idTag.parent'),
      cell: (tag) =>
        tag.parentIdTag ? (
          <span className="font-mono text-xs">{tag.parentIdTag}</span>
        ) : (
          '—'
        ),
    },
    {
      key: 'maxTx',
      header: t('idTag.maxActiveTx'),
      cell: (tag) => formatMaxTx(tag, t),
    },
    {
      key: 'expiry',
      header: t('idTag.expiry'),
      cell: (tag) =>
        tag.expiresAt ? formatDate(tag.expiresAt, 'yyyy-MM-dd') : t('idTag.never'),
    },
    {
      key: 'owner',
      header: t('idTag.owner'),
      sortable: true,
      sortKey: 'user',
      sortValue: (tag) => tag.ownerName ?? tag.ownerEmail,
      cell: (tag) =>
        tag.userId ? (
          <Link to="/users/$userId" params={{ userId: tag.userId }} className="text-primary hover:underline">
            {tag.ownerName ?? tag.ownerEmail ?? tag.userId}
          </Link>
        ) : (
          '—'
        ),
    },
    {
      key: 'status',
      header: t('idTag.status'),
      cell: (tag) => (
        <Badge variant={STATUS_VARIANT[tag.status]}>{t(`idTag.status.${tag.status}`)}</Badge>
      ),
    },
    {
      key: 'created',
      header: t('user.createdAt'),
      sortable: true,
      sortValue: (tag) => tag.createdAt,
      cell: (tag) => formatDate(tag.createdAt),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12 text-right',
      cell: (tag) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={t('actions.more')}>
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setEditTag(tag)}>
              <Pencil className="mr-2 h-4 w-4" />
              {t('idTag.update')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setExtendTag(tag)}>
              <CalendarPlus className="mr-2 h-4 w-4" />
              {tag.expiresAt ? t('idTag.extendExpiry') : t('idTag.addExpiry')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {isTagBlocked(tag) ? (
              <DropdownMenuItem
                onClick={() =>
                  unblock.mutate(tag.id, {
                    onSuccess: () => toast.success(t('idTag.unblocked')),
                    onError: (e) => toast.error(resolveApiError(e, t)),
                  })
                }
              >
                <ShieldCheck className="mr-2 h-4 w-4" />
                {t('idTag.unblock')}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onClick={() =>
                  block.mutate(tag.id, {
                    onSuccess: () => toast.success(t('idTag.blocked')),
                    onError: (e) => toast.error(resolveApiError(e, t)),
                  })
                }
              >
                <Ban className="mr-2 h-4 w-4" />
                {t('idTag.block')}
              </DropdownMenuItem>
            )}
            {tag.isPhysical ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => setDeleteTag(tag)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('actions.delete')}
                </DropdownMenuItem>
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const confirmDelete = () => {
    if (!deleteTag) return;
    del.mutate(deleteTag.id, {
      onSuccess: () => {
        toast.success(t('idTag.deleted'));
        setDeleteTag(null);
      },
      onError: (e) => toast.error(resolveApiError(e, t)),
    });
  };

  return (
    <>
      <PageHeader
        title={t('nav.idTags')}
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('idTag.create')}
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[200px] flex-1">
          <div className="relative">
            <Search className="text-muted-foreground absolute top-2.5 left-2.5 h-4 w-4" />
            <Input
              className="pl-9"
              placeholder={t('actions.search')}
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>
        <div className="w-full space-y-1.5 sm:w-56">
          <label className="text-muted-foreground text-xs font-medium">
            {t('idTag.filterByUser')}
          </label>
          <SelectField
            value={userId}
            options={userFilterOptions}
            onValueChange={(v) => {
              setUserId(v);
              setPage(1);
            }}
          />
        </div>
      </div>

      <DataTable
        data={list.data?.data}
        columns={columns}
        loading={list.isLoading}
        rowKey={(tag) => tag.id}
        emptyTitle={t('idTag.notFound')}
        emptyDescription={t('idTag.notFoundHint')}
        sort={sort}
        onSortChange={onSortChange}
      />

      {list.data && (list.data.totalCount ?? 0) > 0 ? (
        <Pagination
          page={page}
          totalPages={list.data.totalPages ?? 1}
          totalCount={list.data.totalCount ?? 0}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          loading={list.isFetching}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          labels={labels}
        />
      ) : null}

      <CreateIdTagDialog open={creating} onOpenChange={setCreating} />
      <UpdateIdTagDialog tag={editTag} open={Boolean(editTag)} onOpenChange={(o) => !o && setEditTag(null)} />
      <ExtendIdTagExpiryDialog
        tag={extendTag}
        open={Boolean(extendTag)}
        onOpenChange={(o) => !o && setExtendTag(null)}
      />

      {deleteTag ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-background max-w-md rounded-lg border p-6 shadow-lg">
            <h3 className="text-lg font-semibold">{t('idTag.deleteTitle')}</h3>
            <p className="text-muted-foreground mt-2 text-sm">{t('idTag.deleteConfirm')}</p>
            <p className="mt-2 font-mono text-sm">{deleteTag.value}</p>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteTag(null)}>
                {t('actions.cancel')}
              </Button>
              <Button variant="destructive" onClick={confirmDelete} disabled={del.isPending}>
                {t('actions.delete')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
